/**
 * Local Authentication Service
 * Replaces external ant-design-pro API calls with local implementation
 */

// Storage keys
const STORAGE_KEY_USER = 'cargomind_current_user';
const STORAGE_KEY_SESSION = 'cargomind_session';

/**
 * Get current user from localStorage
 */
export async function currentUser(options?: {
	[key: string]: any;
}): Promise<{ data: API.CurrentUser }> {
	try {
		const userStr = localStorage.getItem(STORAGE_KEY_USER);
		const sessionStr = localStorage.getItem(STORAGE_KEY_SESSION);

		// Check if user session exists
		if (userStr && sessionStr) {
			const user = JSON.parse(userStr);
			return { data: user };
		}

		// No user session found
		throw new Error('No user session found');
	} catch (error) {
		// console.error('Error fetching current user:', error);
		throw error;
	}
}

/**
 * Login with username and password
 */
export async function login(
	body: API.LoginParams,
	options?: { [key: string]: any },
): Promise<API.LoginResult> {
	try {
		const { username, password, autoLogin } = body;

		// Simple validation - in development mode, accept any credentials
		// In production, this would connect to your actual backend
		if (!username || !password) {
			return {
				status: 'error',
				type: 'account',
				currentAuthority: 'guest',
			};
		}

		// Mock successful login
		const mockUser: API.CurrentUser = {
			name: username,
			userid: `user_${username}`,
			email: `${username}@cargomind.local`,
			avatar: undefined,
			access: 'admin',
			signature: 'CargoMind User',
			title: 'Developer',
		};

		// Store user data
		localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(mockUser));
		localStorage.setItem(
			STORAGE_KEY_SESSION,
			JSON.stringify({
				token: `mock_token_${Date.now()}`,
				expiresAt: Date.now() + 24 * 60 * 60 * 1000, // 24 hours
			}),
		);

		return {
			status: 'ok',
			type: 'account',
			currentAuthority: 'admin',
		};
	} catch (error) {
		console.error('Login error:', error);
		return {
			status: 'error',
			type: 'account',
			currentAuthority: 'guest',
		};
	}
}

/**
 * Logout - clear local session
 */
export async function outLogin(options?: {
	[key: string]: any;
}): Promise<Record<string, any>> {
	try {
		// Clear user session
		localStorage.removeItem(STORAGE_KEY_USER);
		localStorage.removeItem(STORAGE_KEY_SESSION);

		return {
			success: true,
			message: 'Logged out successfully',
		};
	} catch (error) {
		console.error('Logout error:', error);
		return {
			success: false,
			message: 'Logout failed',
		};
	}
}

/**
 * Store user from external auth (like Okta)
 */
export function storeExternalUser(user: API.CurrentUser): void {
	localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(user));
	localStorage.setItem(
		STORAGE_KEY_SESSION,
		JSON.stringify({
			token: `external_${Date.now()}`,
			expiresAt: Date.now() + 24 * 60 * 60 * 1000, // 24 hours
		}),
	);
}

/**
 * Check if user session is valid
 */
export function isSessionValid(): boolean {
	try {
		const sessionStr = localStorage.getItem(STORAGE_KEY_SESSION);
		if (!sessionStr) {
			return false;
		}

		const session = JSON.parse(sessionStr);
		return session.expiresAt > Date.now();
	} catch (error) {
		return false;
	}
}
